import type { ExportedCourse, Lesson } from './course';
import type { LessonProgress } from './progress';
import {
  calculateProgressFromBlocks,
  selectLessonProgress,
} from './progress';

export interface CurrentLocation {
  lessonId: string;
}

export interface LessonNavigationItem {
  id: string;
  title: string;
}

type LessonNavigationSource = Pick<Lesson, 'id' | 'title'>;

export function buildLessonNavigationItems(
  lessons: readonly LessonNavigationSource[]
): LessonNavigationItem[] {
  return lessons.map((lesson) => ({
    id: lesson.id,
    title: lesson.title,
  }));
}

export function buildSingleLessonNavigationItems(
  lessonId: string | null | undefined,
  lessonTitle: string | null | undefined
): LessonNavigationItem[] {
  return [
    {
      id: lessonId || 'lesson',
      title: lessonTitle || 'Untitled Lesson',
    },
  ];
}

export function buildCourseLessonProgressMap(
  course: ExportedCourse,
  blockCompletion: Record<string, boolean>,
  engagedLessonIds?: Record<string, true>
): Record<string, LessonProgress> {
  const progressMap: Record<string, LessonProgress> = {};

  for (const lesson of course.lessons) {
    progressMap[lesson.id] = selectLessonProgress(
      course,
      lesson.id,
      blockCompletion,
      Boolean(engagedLessonIds?.[lesson.id])
    );
  }

  return progressMap;
}

export function buildSingleLessonProgressMap(
  lessonId: string,
  blockOrder: string[],
  blockCompletion: Record<string, boolean>,
  isEngaged: boolean = false,
  lessonCompleteBlockId: string | null = null
): Record<string, LessonProgress> {
  return {
    [lessonId]: calculateProgressFromBlocks(
      lessonId,
      blockOrder,
      blockCompletion,
      isEngaged,
      lessonCompleteBlockId
    ),
  };
}

export function getFirstLessonLocation(
  lessons: readonly LessonNavigationSource[]
): CurrentLocation | null {
  const firstLesson = lessons[0];

  if (!firstLesson) {
    return null;
  }

  return { lessonId: firstLesson.id };
}

export function getNextLessonLocation(
  lessons: readonly LessonNavigationSource[],
  currentLocation: CurrentLocation | null
): CurrentLocation | null {
  if (!currentLocation) {
    return null;
  }

  const currentLessonIndex = lessons.findIndex(
    (lesson) => lesson.id === currentLocation.lessonId
  );

  if (currentLessonIndex < 0 || currentLessonIndex >= lessons.length - 1) {
    return null;
  }

  const nextLesson = lessons[currentLessonIndex + 1];
  return nextLesson ? { lessonId: nextLesson.id } : null;
}

/**
 * Find the next lesson the learner should jump to from the Lesson Completion
 * block's "Continue" button. Scans forward from the lesson after `currentLocation`,
 * wraps to the start of the course, and returns the first lesson whose
 * `LessonProgress.status !== 'completed'`. Returns `null` if every other lesson
 * is already completed.
 */
export function getNextIncompleteLessonLocation(
  lessons: readonly LessonNavigationSource[],
  currentLocation: CurrentLocation | null,
  lessonProgressMap: Record<string, LessonProgress>
): CurrentLocation | null {
  if (lessons.length === 0) {
    return null;
  }

  const currentIndex = currentLocation
    ? lessons.findIndex((lesson) => lesson.id === currentLocation.lessonId)
    : -1;

  for (let offset = 1; offset <= lessons.length; offset++) {
    const idx = (currentIndex + offset + lessons.length) % lessons.length;
    if (idx === currentIndex) continue;
    const lesson = lessons[idx];
    if (!lesson) continue;
    const progress = lessonProgressMap[lesson.id];
    if (!progress || progress.status !== 'completed') {
      return { lessonId: lesson.id };
    }
  }

  return null;
}

export function getPreviousLessonLocation(
  lessons: readonly LessonNavigationSource[],
  currentLocation: CurrentLocation | null
): CurrentLocation | null {
  if (!currentLocation) {
    return null;
  }

  const currentLessonIndex = lessons.findIndex(
    (lesson) => lesson.id === currentLocation.lessonId
  );

  if (currentLessonIndex <= 0) {
    return null;
  }

  const previousLesson = lessons[currentLessonIndex - 1];
  return previousLesson ? { lessonId: previousLesson.id } : null;
}

export function findLessonByLocation<T extends LessonNavigationSource>(
  lessons: readonly T[],
  currentLocation: CurrentLocation | null
): T | null {
  if (!currentLocation) {
    return null;
  }

  return lessons.find((lesson) => lesson.id === currentLocation.lessonId) ?? null;
}
